import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Reveal from "../components/site/Reveal";
import Seo, { ORG_JSONLD } from "../components/site/Seo";
import { IMAGES, DIVISIONS } from "../lib/site-data";

export default function About() {
  return (
    <main data-testid="page-about" className="overflow-hidden">
      <Seo
        title="About Ocean Oasis | A Somali-Owned Group of Three Divisions"
        description="Ocean Oasis is a Somali-owned conglomerate built in Mogadishu — a premium supermarket, an international logistics company and a specialty coffee house, all held to one world-class standard."
        path="/about"
        jsonLd={ORG_JSONLD}
      />
      {/* HERO */}
      <section className="relative pt-40 pb-20 md:pb-28 bg-[#faf6f0]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-label text-[#f59e0b] mb-6"
              >
                About · The Ocean Oasis Group
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="font-display text-[#0a3d7a] leading-[0.92]"
                style={{ fontSize: "clamp(2.8rem, 8vw, 7rem)" }}
              >
                Built in Mogadishu. <em className="italic text-[#f59e0b]">For the world.</em>
              </motion.h1>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION SPLIT */}
      <section className="pb-24 md:pb-32 bg-[#faf6f0]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-6 img-zoom aspect-[4/5] overflow-hidden">
              <img
                src={IMAGES.heroSkyline}
                alt="Mogadishu coastline — home city of the Ocean Oasis Group"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </Reveal>
            <div className="lg:col-span-5 lg:col-start-8 space-y-8">
              <Reveal delay={0.1}>
                <p className="font-label text-[#f59e0b]">Our Mission</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl mt-4 text-[#0a3d7a] leading-[1.05]">
                  To bring world-class standards to everyday Somali life — and to carry Somali business out to the world.
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="font-body text-base text-[#0a3d7a]/80 leading-relaxed">
                  Ocean Oasis is a Somali-owned conglomerate operating three
                  distinct businesses out of Sundus Plaza in Bondhere District,
                  Mogadishu. We exist for one reason: to prove that premium,
                  reliable, considered experiences belong here — and to make
                  them the default.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="font-body text-base text-[#0a3d7a]/70 leading-relaxed">
                  Whether you arrive to stock a household, ship a container, or
                  simply find a quiet corner over a good coffee — we want the
                  experience to feel inevitable, calm and properly done.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-24 md:py-32 bg-[#0a3d7a] text-[#faf6f0]">
        <div className="container-x">
          <Reveal>
            <div className="max-w-5xl">
              <p className="font-label text-[#f59e0b] mb-8">A Guiding Idea</p>
              <blockquote className="font-display italic text-3xl md:text-5xl lg:text-7xl leading-[1.05]">
                "Excellence shouldn't be imported one product at a time. It
                should be the air the city breathes."
              </blockquote>
              <p className="font-label text-[#faf6f0]/70 mt-10">
                — The Ocean Oasis founding principle
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DIVISIONS RECAP */}
      <section className="py-24 md:py-32 bg-[#fdf9f2]">
        <div className="container-x">
          <Reveal>
            <div className="grid lg:grid-cols-12 gap-10 mb-14">
              <div className="lg:col-span-6">
                <p className="font-label text-[#f59e0b]">Three Divisions</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#0a3d7a] leading-tight">
                  One group. <em className="italic">Distinct expertise.</em>
                </h2>
              </div>
              <div className="lg:col-span-5 lg:col-start-8 lg:pt-3">
                <p className="font-body text-base text-[#0a3d7a]/70 leading-relaxed">
                  Each division has its own team, its own discipline, and its
                  own standard of craft — held together by a shared, very
                  Somali sense of hospitality.
                </p>
              </div>
            </div>
          </Reveal>
          <div className="grid gap-px bg-[rgba(10,61,122,0.15)] border border-[rgba(10,61,122,0.15)]">
            {DIVISIONS.map((d, i) => (
              <Reveal key={d.slug} delay={i * 0.1}>
                <Link
                  to={d.href}
                  data-testid={`about-division-${d.slug}`}
                  className="group grid lg:grid-cols-12 gap-6 items-center bg-[#fdf9f2] hover:bg-[#0a3d7a] hover:text-[#faf6f0] transition-all duration-500 p-8 md:p-10"
                >
                  <p className="lg:col-span-1 font-label text-[#f59e0b]">
                    {d.label}
                  </p>
                  <div className="lg:col-span-3">
                    <h3 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0a3d7a] group-hover:text-[#faf6f0] transition-colors leading-tight">
                      {d.short}
                    </h3>
                  </div>
                  <p className="lg:col-span-6 font-body text-base text-[#0a3d7a]/75 group-hover:text-[#faf6f0]/85 transition-colors leading-relaxed">
                    {d.blurb}
                  </p>
                  <div className="lg:col-span-2 flex lg:justify-end">
                    <span className="inline-flex items-center gap-2 font-label text-[#0a3d7a] group-hover:text-[#f59e0b] transition-colors">
                      Explore <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#faf6f0]">
        <div className="container-x">
          <Reveal>
            <div className="max-w-3xl">
              <p className="font-label text-[#f59e0b] mb-5">Come Find Us</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0a3d7a] leading-[1.02] mb-8">
                Sundus Plaza, <em className="italic">Bondhere District.</em>
              </h2>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="about-cta-contact" className="btn-primary">
                  Visit & Contact <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
